import React, { useState, useEffect } from 'react';
import {
    Card, Table, Button, message, Tag, Space, Modal, Form, Input, Select, DatePicker, Badge,
    Row, Col, Empty, Spin, Popconfirm, Alert
} from 'antd';
import {
    BellOutlined, PlusOutlined, DeleteOutlined, CheckOutlined, EyeOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { notificationService, patientService } from '../../services';
import { useApp } from '../../context/AppContext';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;

const Notifications = () => {
    const { t } = useTranslation(['notification', 'common']);
    const { user } = useApp();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [diagnosticLoading, setDiagnosticLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [patients, setPatients] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [statusFilter, setStatusFilter] = useState(''); // Empty = All Status
    const [dateFilter, setDateFilter] = useState('all'); // 'all', 'today', 'upcoming'
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [form] = Form.useForm();

    useEffect(() => {
        fetchNotifications();
        fetchPatients();
    }, [statusFilter, dateFilter]);

    const fetchNotifications = async (page = 1) => {
        setLoading(true);
        try {
            const response = await notificationService.getAll({
                status: statusFilter || undefined,
                page,
                limit: 10
            });

            console.log('📬 API Response:', response);

            if (response.success) {
                // Handle both array and object responses
                let notificationsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);

                console.log('📬 Type of response.data:', typeof response.data);
                console.log('📬 Is array?:', Array.isArray(response.data));
                console.log('📬 Actual notifications array:', notificationsData);
                console.log('📬 Fetched notifications count:', notificationsData.length);

                let filteredData = notificationsData;

                // Apply date filter on client side
                if (dateFilter === 'today') {
                    const today = dayjs().startOf('day');
                    filteredData = filteredData.filter(notif => {
                        const notifDate = dayjs(notif.notificationDate).startOf('day');
                        const matches = notifDate.isSame(today);
                        console.log(`  Notification ${notif._id}: ${dayjs(notif.notificationDate).format('YYYY-MM-DD')} ${matches ? '✓ TODAY' : '✗ not today'}`);
                        return matches;
                    });
                    console.log(`After today filter: ${filteredData.length} notifications`);
                } else if (dateFilter === 'upcoming') {
                    const today = dayjs().startOf('day');
                    filteredData = filteredData.filter(notif => {
                        const notifDate = dayjs(notif.notificationDate).startOf('day');
                        return notifDate.isAfter(today);
                    });
                }

                console.log('📬 Final filtered data:', filteredData);
                setNotifications(filteredData);
                setPagination({
                    current: response.pagination?.page || 1,
                    pageSize: response.pagination?.limit || 10,
                    total: response.pagination?.total || filteredData.length
                });
            } else {
                console.error('API returned success: false', response);
                message.error(response.message || t('failedToFetch'));
            }
        } catch (error) {
            console.error('❌ Error fetching notifications:', error);
            message.error(t('failedToFetch') + ': ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchPatients = async () => {
        try {
            const response = await patientService.getAll({ limit: 1000 });
            if (response.success) {
                setPatients(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch patients');
        }
    };

    const checkDentistProfile = async () => {
        setDiagnosticLoading(true);
        try {
            const response = await fetch('/api/notifications/debug/profile', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            const data = await response.json();

            if (data.success) {
                message.success(`✓ Dentist profile found! ID: ${data.dentistId}`);
                console.log('✓ Dentist Profile:', data);
            } else {
                message.error(`❌ ${data.message}`);
                console.error('Dentist Profile Issue:', data);
            }
        } catch (error) {
            message.error('Failed to check dentist profile: ' + error.message);
            console.error(error);
        } finally {
            setDiagnosticLoading(false);
        }
    };

    const handleCreateNotification = async (values) => {
        setSubmitting(true);
        try {
            const notificationData = {
                patientId: values.patientId,
                title: values.title,
                description: values.description,
                notificationDate: values.notificationDate.toISOString(),
                notificationType: values.notificationType,
                priority: values.priority,
                notes: values.notes
            };
            await notificationService.create(notificationData);
            message.success(t('createdSuccess'));
            setModalVisible(false);
            form.resetFields();
            fetchNotifications();
        } catch (error) {
            message.error(error.response?.data?.message || t('failedToCreate'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleMarkAsRead = async (id) => {
        try {
            await notificationService.markAsRead(id);
            message.success(t('markedAsReadSuccess'));
            fetchNotifications();
        } catch (error) {
            message.error(t('failedToMarkRead'));
        }
    };

    const handleComplete = async (id) => {
        try {
            await notificationService.complete(id);
            message.success(t('completedSuccess'));
            fetchNotifications();
        } catch (error) {
            message.error('Failed to complete notification');
        }
    };

    const handleDelete = async (id) => {
        try {
            await notificationService.delete(id);
            message.success(t('deletedSuccess'));
            fetchNotifications();
        } catch (error) {
            message.error(t('failedToDelete'));
        }
    };

    // Helper function to translate notification titles that might be translation keys
    const translateTitle = (titleValue) => {
        if (!titleValue) return 'N/A';

        // Check if the title is a translation key (starts with key pattern)
        const keyPattern = /^[a-z]+\.[a-zA-Z]+$/;
        if (keyPattern.test(titleValue)) {
            return t(titleValue);
        }

        // Otherwise, return the title as-is
        return titleValue;
    };

    const columns = [
        {
            title: t('patient'),
            dataIndex: ['patientId', 'name'],
            key: 'patient',
            render: (name) => name || 'Unknown'
        },
        {
            title: t('title'),
            dataIndex: 'title',
            key: 'title',
            render: (title) => translateTitle(title)
        },
        {
            title: t('type'),
            dataIndex: 'notificationType',
            key: 'type',
            render: (type) => (
                <Tag color="blue">{type}</Tag>
            )
        },
        {
            title: t('priority'),
            dataIndex: 'priority',
            key: 'priority',
            render: (priority) => {
                const colors = {
                    'High': 'red',
                    'Medium': 'orange',
                    'Low': 'green'
                };
                return <Tag color={colors[priority]}>{priority}</Tag>;
            }
        },
        {
            title: t('date'),
            dataIndex: 'notificationDate',
            key: 'date',
            render: (date) => dayjs(date).format('MMM DD, YYYY HH:mm')
        },
        {
            title: t('status'),
            dataIndex: 'isRead',
            key: 'isRead',
            render: (isRead) => (
                <Badge status={isRead ? 'success' : 'processing'} text={isRead ? t('read') : t('unread')} />
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space size="small">
                    {!record.isRead && (
                        <Button
                            type="link"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => handleMarkAsRead(record._id)}
                        >
                            {t('markRead')}
                        </Button>
                    )}
                    {record.status === 'Pending' && (
                        <Button
                            type="link"
                            size="small"
                            icon={<CheckOutlined />}
                            onClick={() => handleComplete(record._id)}
                        >
                            {t('complete')}
                        </Button>
                    )}
                    <Popconfirm
                        title={t('deleteConfirm')}
                        description={t('deleteConfirmDesc')}
                        onConfirm={() => handleDelete(record._id)}
                        okText={t('common:yes')}
                        cancelText={t('common:no')}
                    >
                        <Button type="link" danger size="small" icon={<DeleteOutlined />}>
                            {t('delete')}
                        </Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div>
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h1>
                    <BellOutlined style={{ marginRight: 8 }} />
                    {t('title')}
                </h1>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setModalVisible(true)}
                >
                    {t('newNotification')}
                </Button>
            </div>

            {/* Filter */}
            <Card style={{ marginBottom: 16 }}>
                <Alert
                    message={`💡 ${t('troubleshooting')}`}
                    description={
                        <div>
                            <ul style={{ marginBottom: 12, paddingLeft: 20 }}>
                                <li>{t('troubleStep1')}</li>
                                <li>{t('troubleStep2')}</li>
                                <li>{t('troubleStep3')}</li>
                                <li>{t('troubleStep4')}</li>
                            </ul>
                            <Button
                                size="small"
                                onClick={checkDentistProfile}
                                loading={diagnosticLoading}
                                type="primary"
                            >
                                🔍 {t('checkDentistProfile')}
                            </Button>
                        </div>
                    }
                    type="info"
                    closable
                    style={{ marginBottom: 16 }}
                />

                <Row gutter={16}>
                    <Col xs={24} sm={12} md={8}>
                        <div style={{ marginBottom: 12 }}>
                            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>{t('status')}</label>
                            <Select
                                value={statusFilter}
                                onChange={setStatusFilter}
                                style={{ width: '100%' }}
                            >
                                <Option value="">{t('allStatus')}</Option>
                                <Option value="Pending">{t('pending')}</Option>
                                <Option value="Completed">{t('completed')}</Option>
                                <Option value="Cancelled">{t('cancelled')}</Option>
                            </Select>
                        </div>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <div style={{ marginBottom: 12 }}>
                            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>{t('show')}</label>
                            <Select
                                value={dateFilter}
                                onChange={setDateFilter}
                                style={{ width: '100%' }}
                            >
                                <Option value="all">{t('allDates')}</Option>
                                <Option value="today">{t('todayOnly')}</Option>
                                <Option value="upcoming">{t('upcomingOnly')}</Option>
                            </Select>
                        </div>
                    </Col>
                </Row>
            </Card>

            {/* Notifications Table */}
            <Card loading={loading}>
                {notifications && notifications.length > 0 ? (
                    <Table
                        columns={columns}
                        dataSource={notifications}
                        rowKey="_id"
                        pagination={{
                            ...pagination,
                            onChange: (page) => fetchNotifications(page),
                            showSizeChanger: true
                        }}
                        scroll={{ x: 'max-content' }}
                    />
                ) : (
                    <Empty
                        description={
                            <div>
                                <p>{t('noNotifications')}</p>
                                <p style={{ fontSize: 12, color: '#999', marginTop: 8 }}>
                                    💡 {t('createInstructions')}:
                                </p>
                                <ol style={{ fontSize: 12, color: '#999', textAlign: 'left', display: 'inline-block' }}>
                                    <li>{t('step1')}</li>
                                    <li>{t('step2')}</li>
                                    <li>{t('step3')}</li>
                                    <li>{t('step4')}</li>
                                    <li>{t('step5')}</li>
                                </ol>
                                <p style={{ fontSize: 12, color: '#999', marginTop: 8 }}>
                                    Or click "{t('newNotification')}" button above
                                </p>
                            </div>
                        }
                    />
                )}
            </Card>

            {/* {t("notification.newNotification")} Modal */}
            <Modal
                title={t('setNewNotification')}
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    form.resetFields();
                }}
                footer={null}
                width={600}
            >
                <Form form={form} layout="vertical" onFinish={handleCreateNotification}>
                    <Form.Item
                        name="patientId"
                        label={t('patient')}
                        rules={[{ required: true, message: 'Please select a patient' }]}
                    >
                        <Select placeholder="Select patient" showSearch optionFilterProp="children">
                            {patients.map(p => (
                                <Option key={p._id} value={p._id}>
                                    {p.name} - {p.contact}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="notificationDate"
                        label={t('date')}
                        rules={[{ required: true, message: 'Please select notification date' }]}
                    >
                        <DatePicker
                            showTime
                            format="YYYY-MM-DD HH:mm"
                            style={{ width: '100%' }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="notificationType"
                        label={t('type')}
                        initialValue="Follow-up"
                    >
                        <Select>
                            <Option value="Follow-up">Follow-up</Option>
                            <Option value="Filling">Filling</Option>
                            <Option value="Cleaning">Cleaning</Option>
                            <Option value="Root Canal">Root Canal</Option>
                            <Option value="Extraction">Extraction</Option>
                            <Option value="Crown">Crown</Option>
                            <Option value="Bridge">Bridge</Option>
                            <Option value="Implant">Implant</Option>
                            <Option value="Whitening">Whitening</Option>
                            <Option value="Checkup">Checkup</Option>
                            <Option value="Other">Other</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="priority"
                        label={t('priority')}
                        initialValue="Medium"
                    >
                        <Select>
                            <Option value="Low">{t("low")}</Option>
                            <Option value="Medium">{t("medium")}</Option>
                            <Option value="High">{t("high")}</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item name="title" label="Notification Title">
                        <Input placeholder="Enter notification title (optional)" />
                    </Form.Item>

                    <Form.Item name="description" label={t('description')}>
                        <TextArea
                            rows={3}
                            placeholder="Enter details about the notification"
                        />
                    </Form.Item>

                    <Form.Item name="notes" label={t('notes')}>
                        <TextArea
                            rows={2}
                            placeholder="Additional notes"
                        />
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" block loading={submitting}>
                            {t("newNotification")}
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Notifications;
